import {
  CallScribeCallStatus,
  CallScribeConsentResult,
  CallScribePipelineStage,
  CallScribeTimeEntryStatus,
} from 'services/callScribe/types';

export const AUTO_POST_TITLE = 'Posting automatically';

export const AUTO_POST_BODY = 'This entry goes to the ticket on its own.';

export const AUTO_POST_INVITE = 'Change the customer-facing notes to post your version instead.';

export const CUSTOMER_NOTES_OFF = 'Customer notes are turned off for this company.';

export const autoPostLine = (customerNotesPosted: boolean): string =>
  (customerNotesPosted ? `${AUTO_POST_BODY} ${AUTO_POST_INVITE}` : AUTO_POST_BODY);

export type TCustomerNotesState = 'loading' | 'on' | 'off';

export const customerNotesState = (
  options: { noteOverrides?: { customerNoteEnabled?: boolean | null } } | undefined,
  optionsError: string | null | undefined,
  crmSourceId: number | null | undefined,
): TCustomerNotesState => {
  if (options) return options.noteOverrides?.customerNoteEnabled === false ? 'off' : 'on';
  const canLoad = typeof crmSourceId === 'number' && crmSourceId > 0;
  return canLoad && !optionsError ? 'loading' : 'on';
};

export const NOTES_LOADING_LABEL = 'Loading the customer-facing notes';

export type PostCallReviewPhase =
  | 'pending'
  | 'generating'
  | 'unavailable'
  | 'ready'
  | 'failed'
  | 'declined'
  | 'notConnected'
  | 'posted'
  | 'discarded';

export interface IReviewPhaseInput {
  hasIntelligence: boolean | undefined;
  /**
   * Whether the server will post this call's time entry on its own. Resolved server-side from the
   * same inputs the trigger uses — the tenant default, the technician's effective setting and the
   * routing verdict for this call — and never re-derived here. A second copy of that rule in the
   * pod would drift, and the drift would show as the panel promising an automatic post for a call
   * the router declines.
   */
  willPostAutomatically?: boolean | null | undefined;
  pipelineStage: CallScribePipelineStage | undefined | null;
  reconcileExhausted?: boolean;
  linkStatus: CallScribeTimeEntryStatus | undefined | null;
  /**
   * True when the call carries no PSA ticket AND the technician has not named one. Until a ticket
   * is named there is nowhere to post, and the server refuses one, so every submit path is closed
   * rather than offering a control whose only outcome is a Failed link. Naming a ticket lifts all
   * of them at once.
   */
  ticketless?: boolean;
  consentResult: CallScribeConsentResult | undefined | null;
  status?: CallScribeCallStatus | undefined | null;
  answeredAtUtc?: string | undefined | null;
}

/**
 * Two questions, deliberately separated, because conflating them is what produced the defect.
 *
 * "Can this be submitted?" must NEVER depend on a status allowlist — that is what denied the time entry
 * Masood asked for. It depends only on the phase, and every phase that is not posted, discarded or
 * genuinely in flight is actionable.
 *
 * "Can we tell the technician nobody answered?" does depend on the status, and here an allowlist is
 * correct rather than a liability: saying "nobody answered" is a claim, and we can only make it for a
 * status that reports a real outcome. `Unknown` is an admission of ignorance, and so is a value this
 * build has never seen — both must degrade to the honest "we cannot tell the two apart" copy, not to a
 * confident sentence. A miss here now costs a vaguer headline and nothing else.
 *
 * `Completed` is in the set because it is the LARGEST never-answered bucket on dev2 — 244 calls with a
 * null answeredAtUtc — and the previous allowlist omitted it, which is how a never-answered call could
 * be classified as a normal one.
 */
const KNOWN_UNANSWERED_OUTCOMES = new Set<CallScribeCallStatus>([
  CallScribeCallStatus.Completed,
  CallScribeCallStatus.Failed,
  CallScribeCallStatus.NoAnswer,
  CallScribeCallStatus.Busy,
  CallScribeCallStatus.Cancelled,
]);

const IN_FLIGHT_STAGES = new Set<CallScribePipelineStage>([
  CallScribePipelineStage.Received,
  CallScribePipelineStage.Transcribing,
  CallScribePipelineStage.Summarizing,
]);

export const isInFlightStage = (
  stage: CallScribePipelineStage | null | undefined,
): boolean => stage != null && IN_FLIGHT_STAGES.has(stage);

const neverConnected = (input: IReviewPhaseInput): boolean =>
  input.status != null
  && KNOWN_UNANSWERED_OUTCOMES.has(input.status)
  && !input.answeredAtUtc;

/**
 * Whether to offer the technician a manual submit control.
 *
 * The rule is deliberately not "is this call automatic" but "is the automatic path still going to
 * deal with it". A trigger that fired and was REJECTED by the PSA has left a failed entry and no
 * longer owns the call, so the control has to come back — hiding it there would leave the entry
 * unrecoverable from the UI, which is worse than the duplicate button being fixed.
 *
 * A missing `willPostAutomatically` reads as false, which shows the control. That is the safe
 * direction: the failure is a redundant button, not an entry nobody can post.
 */
export const showsManualSubmit = (input: IReviewPhaseInput): boolean => {
  if (input.ticketless === true) {
    return false;
  }

  if (input.willPostAutomatically !== true) {
    return true;
  }

  // `willPostAutomatically` is a PREDICTION, and without an expiry it strands the technician: a
  // trigger that is declined, crashes, or is never scheduled leaves linkStatus null forever, so the
  // control would never come back and the entry could not be posted at all. That is worse than the
  // redundant button this hides. `reconcileExhausted` is the falsifier — once we have stopped
  // looking and nothing posted, the automatic path no longer credibly owns the call.
  if (input.reconcileExhausted === true && input.linkStatus == null) {
    return true;
  }

  return input.linkStatus === CallScribeTimeEntryStatus.Failed;
};

/**
 * Whether to offer a control that writes the entry now.
 *
 * `showsManualSubmit` answers "is this the technician's job at all". This answers the narrower
 * question the panel actually needs, and the difference is the technician's own edit: on the
 * automatic path there is nothing to submit until they change something, and the moment they do,
 * their text is a thing the trigger does not know about. Offering the control only then is what
 * keeps the panel quiet without making an edit unpostable.
 *
 * Guarded on an empty `linkStatus` so an edit made while looking at an entry that is already on the
 * ticket cannot offer to post a second one — the same boundary the reconcile expiry uses.
 */
export const showsSubmitControl = (
  input: IReviewPhaseInput,
  technicianEditedTheEntry: boolean,
): boolean =>
  input.ticketless !== true &&
  (showsManualSubmit(input) || (technicianEditedTheEntry && input.linkStatus == null));

/**
 * True while the automatic post owns the call and has not finished — between hangup and the
 * trigger firing, the pipeline is still running and nothing has posted yet. It is neither "submit
 * this yourself" nor "posted", and offering a control here would race the trigger.
 */
const UNANSWERED_ATTEMPTS = new Set<CallScribeCallStatus>([
  CallScribeCallStatus.NoAnswer,
  CallScribeCallStatus.Busy,
  CallScribeCallStatus.Cancelled,
]);

export const isLoggedAttempt = (input: IReviewPhaseInput): boolean =>
  input.willPostAutomatically === true &&
  !input.answeredAtUtc &&
  input.status != null &&
  UNANSWERED_ATTEMPTS.has(input.status);

export const entryStillOwed = (linkStatus: CallScribeTimeEntryStatus | null | undefined): boolean =>
  linkStatus === null ||
  linkStatus === undefined ||
  linkStatus === CallScribeTimeEntryStatus.Suggested ||
  linkStatus === CallScribeTimeEntryStatus.Claimed;

export const isAwaitingAutomaticPost = (input: IReviewPhaseInput): boolean =>
  input.willPostAutomatically === true
  && input.reconcileExhausted !== true
  && input.linkStatus !== CallScribeTimeEntryStatus.Posted
  && input.linkStatus !== CallScribeTimeEntryStatus.Rejected
  && input.linkStatus !== CallScribeTimeEntryStatus.Failed;

export const resolveReviewPhase = (input: IReviewPhaseInput): PostCallReviewPhase => {
  if (input.linkStatus === CallScribeTimeEntryStatus.Posted) {
    return 'posted';
  }
  if (input.linkStatus === CallScribeTimeEntryStatus.Rejected) {
    return 'discarded';
  }
  if (neverConnected(input)) {
    return 'notConnected';
  }
  if (input.pipelineStage === CallScribePipelineStage.Failed) {
    return 'failed';
  }
  if (input.consentResult === CallScribeConsentResult.Declined) {
    return 'declined';
  }
  if (input.hasIntelligence === true) {
    return 'ready';
  }
  if (input.pipelineStage === CallScribePipelineStage.NotApplicable) {
    return 'unavailable';
  }
  if (isInFlightStage(input.pipelineStage)) {
    return 'generating';
  }
  if (input.pipelineStage == null && input.reconcileExhausted !== true) {
    return 'pending';
  }
  return 'unavailable';
};

/**
 * `notConnected` belongs here, and its absence was a defect rather than a policy: Masood asked for
 * exactly this case — "we should just get the time entries where we tried to call the customer but they
 * didn't pick up" — and an attempted call is billable contact work. Before the classification above was
 * fixed, the never-answered Completed calls reached this predicate as `unavailable` and were actionable
 * by accident, while the 49 correctly identified as never-connected were the ones denied the entry.
 */
export const isReviewActionable = (phase: PostCallReviewPhase): boolean =>
  phase === 'ready'
  || phase === 'failed'
  || phase === 'declined'
  || phase === 'unavailable'
  || phase === 'notConnected'
  || phase === 'pending';

export const isReviewPending = (phase: PostCallReviewPhase): boolean =>
  phase !== 'posted' && phase !== 'discarded';

export const postedDurationOf = (
  call: { postedDurationMinutes?: number | null } | undefined,
): number | undefined => {
  const minutes = call?.postedDurationMinutes;
  return typeof minutes === 'number' && Number.isFinite(minutes) && minutes > 0
    ? Math.round(minutes)
    : undefined;
};

export const fallbackDurationMinutes = (
  suggestedDurationMinutes: number | null | undefined,
  durationSeconds: number | null | undefined,
): number => {
  if (typeof suggestedDurationMinutes === 'number' && suggestedDurationMinutes > 0) {
    return suggestedDurationMinutes;
  }
  if (typeof durationSeconds === 'number' && durationSeconds > 0) {
    return Math.max(1, Math.round(durationSeconds / 60));
  }
  return 0;
};
